import { useEffect, useState } from "react";
import { Button, Input, Toggle } from "../../../shared";
import { useAuth } from "../../../hooks/useAuth";
import { toast } from "react-toastify";
import {
  useGetNotificationPreferencesQuery,
  useUpdateNotificationPreferencesMutation,
} from "../../../redux/services/notifications/notificationApi";

function clone(obj) {
  return JSON.parse(JSON.stringify(obj));
}

const Section = ({ title, subtitle, children }) => (
  <div className="bg-white rounded-md border p-4 space-y-2">
    <div>
      <div className="text-lg font-semibold text-gray-800">{title}</div>
      {subtitle && <div className="text-gray-500 text-sm">{subtitle}</div>}
    </div>
    <div className="pt-2">{children}</div>
  </div>
);

const NotificationPreferencesPage = () => {
  const { user } = useAuth();
  const userId = user?._id;
  const { data, isFetching, refetch } = useGetNotificationPreferencesQuery(
    userId,
    { skip: !userId }
  );
  const [updatePreferences, { isLoading: isSaving }] =
    useUpdateNotificationPreferencesMutation();

  const [prefs, setPrefs] = useState(null);

  useEffect(() => {
    if (data?.data?.preferences) {
      setPrefs(clone(data.data.preferences));
    }
  }, [data]);

  const toggleChannel = (channelKey, enabled) => {
    setPrefs((prev) => {
      const next = clone(prev || {});
      next[channelKey] = { ...(next[channelKey] || {}), enabled };
      return next;
    });
  };

  const updateQuiet = (patch) => {
    setPrefs((prev) => {
      const next = clone(prev || {});
      const quiet = next.quietHours || {};
      next.quietHours = { ...quiet, ...patch };
      return next;
    });
  };

  const handleSave = async () => {
    if (!userId || !prefs) return;
    try {
      await updatePreferences({ userId, preferences: prefs }).unwrap();
      toast.success("Preferences updated");
      await refetch();
    } catch (e) {
      console.error(e);
      toast.error("Failed to update preferences");
    }
  };

  const quiet = prefs?.quietHours || {};

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-2xl font-bold text-gray-800">
          Notification Preferences
        </div>
        <Button onClick={handleSave} disabled={!prefs || isSaving}>
          {isSaving ? "Saving..." : "Save"}
        </Button>
      </div>

      {isFetching && <div className="text-gray-500">Loading...</div>}
      {!isFetching && prefs && (
        <div className="space-y-4">
          <Section
            title="Notification Delivery Channels"
            subtitle="Choose how you want to receive different types of notifications. You can enable or disable each channel below. In-App notifications appear within the Attune Logic platform, Push notifications are sent to your mobile device, and Email notifications are delivered to your inbox."
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="flex items-center justify-between border rounded p-3">
                <div className="text-sm text-gray-700">
                  <span className="font-medium">In-App</span>
                  <span className="block text-xs text-gray-500 mt-1">
                    Receive notifications directly inside the Attune Logic app.
                  </span>
                </div>
                <Toggle
                  id="inApp.enabled"
                  checked={!!prefs?.inApp?.enabled}
                  onChange={({ target }) =>
                    toggleChannel("inApp", target.checked)
                  }
                />
              </div>
              <div className="flex items-center justify-between border rounded p-3">
                <div className="text-sm text-gray-700">
                  <span className="font-medium">Push</span>
                  <span className="block text-xs text-gray-500 mt-1">
                    Get real-time alerts on your mobile device (requires app
                    permissions).
                  </span>
                </div>
                <Toggle
                  id="push.enabled"
                  checked={!!prefs?.push?.enabled}
                  onChange={({ target }) =>
                    toggleChannel("push", target.checked)
                  }
                />
              </div>
              <div className="flex items-center justify-between border rounded p-3">
                <div className="text-sm text-gray-700">
                  <span className="font-medium">Email</span>
                  <span className="block text-xs text-gray-500 mt-1">
                    Receive notifications in your email inbox.
                  </span>
                </div>
                <Toggle
                  id="email.enabled"
                  checked={!!prefs?.email?.enabled}
                  onChange={({ target }) =>
                    toggleChannel("email", target.checked)
                  }
                />
              </div>
            </div>
          </Section>

          <div className="w-full flex flex-col lg:flex-row lg:items-center gap-4 bg-white/80 border border-gray-200 rounded-md shadow-sm p-5 transition-all">
            <div className="flex flex-1 items-center gap-3">
              <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-full bg-primary-100 text-primary-600">
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V4a2 2 0 10-4 0v1.341C7.67 7.165 6 9.388 6 12v2.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                  />
                </svg>
              </div>
              <div>
                <div className="text-base font-medium text-gray-800">
                  Enable Off Hours
                </div>
                <div className="text-xs text-gray-500">
                  Silence notifications during your chosen hours
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Toggle
                  id="quietHours.enabled"
                  checked={!!quiet.enabled}
                  onChange={({ target }) =>
                    updateQuiet({ enabled: target.checked })
                  }
                  className="scale-110"
                />
              </div>
            </div>
            <div className="flex flex-col md:flex-row items-center gap-2 flex-1 justify-start lg:justify-end">
              <Input
                id="quietHours.startTime"
                label="Start"
                type="time"
                size="sm"
                className="w-48 md:w-56"
                value={quiet.startTime || "22:00"}
                onChange={({ value }) => updateQuiet({ startTime: value })}
              />
              <span className="mx-2 text-gray-400 hidden md:inline">—</span>
              <Input
                id="quietHours.endTime"
                label="End"
                type="time"
                size="sm"
                className="w-48 md:w-56"
                value={quiet.endTime || "08:00"}
                onChange={({ value }) => updateQuiet({ endTime: value })}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationPreferencesPage;
